// C:\Ava_Local\Ava_API\avaai_dashboard\frontend\src\pages\education\FlashCardsPage.tsx
import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";

type Operation = "addition" | "subtraction" | "multiplication" | "division";
type GradeBand = "K-2" | "3-5" | "6-8" | "9-12";

type FlashCard = {
  question: string;
  answer: number;
  hint: string;
  example: string;
};

function makeFlashCardStyles(themeColors: any) {
  const pageBg: React.CSSProperties = {
    minHeight: "100vh",
    width: "100%",
    boxSizing: "border-box",
    padding: "16px 12px 16px",
    background: themeColors.bg,
    color: themeColors.text,
  };

  const shellStyle: React.CSSProperties = {
    maxWidth: "1400px",
    margin: "0 auto",
  };

  const panelStyle: React.CSSProperties = {
    borderRadius: "20px",
    padding: "16px 16px",
    background: themeColors.panel,
    border: `1px solid ${themeColors.border}`,
    boxShadow: themeColors.glow,
    backdropFilter: "blur(10px)",
  };

  const cardStyle: React.CSSProperties = {
    borderRadius: "16px",
    padding: "14px",
    background: themeColors.cardBg,
    color: themeColors.text,
    border: `1px solid ${themeColors.border}`,
    boxShadow:
      "0 8px 22px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.03)",
  };

  const controlStyle: React.CSSProperties = {
    background: themeColors.panel,
    color: themeColors.text,
    border: `1px solid ${themeColors.border}`,
    borderRadius: "12px",
    padding: "10px 12px",
    fontSize: "15px",
    outline: "none",
    minHeight: "44px",
    width: "100%",
    boxSizing: "border-box",
  };

  const buttonStyle: React.CSSProperties = {
    minHeight: "44px",
    borderRadius: "12px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.text,
    fontSize: "14px",
    fontWeight: 800,
    cursor: "pointer",
    padding: "0 14px",
    boxShadow: themeColors.glow,
  };

  return {
    pageBg,
    shellStyle,
    panelStyle,
    cardStyle,
    controlStyle,
    buttonStyle,
  };
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getRanges(operation: Operation, grade: GradeBand) {
  const map = {
    "K-2": {
      addition: [0, 20],
      subtraction: [0, 20],
      multiplication: [0, 5],
      division: [1, 5],
    },
    "3-5": {
      addition: [10, 100],
      subtraction: [10, 100],
      multiplication: [2, 12],
      division: [2, 12],
    },
    "6-8": {
      addition: [50, 500],
      subtraction: [50, 500],
      multiplication: [6, 20],
      division: [3, 20],
    },
    "9-12": {
      addition: [100, 1000],
      subtraction: [100, 1000],
      multiplication: [10, 25],
      division: [5, 25],
    },
  } as const;

  const [min, max] = map[grade][operation];
  return { min, max };
}

function buildCard(operation: Operation, grade: GradeBand): FlashCard {
  const { min, max } = getRanges(operation, grade);

  if (operation === "addition") {
    const a = randInt(min, max);
    const b = randInt(min, max);
    return {
      question: `${a} + ${b}`,
      answer: a + b,
      hint: "Try adding the bigger place values first, then the smaller ones.",
      example: `Example: 23 + 15 = (20 + 10) + (3 + 5) = 38.`,
    };
  }

  if (operation === "subtraction") {
    let a = randInt(min, max);
    let b = randInt(min, max);
    if (b > a) [a, b] = [b, a];
    return {
      question: `${a} - ${b}`,
      answer: a - b,
      hint: "Start with the larger number and subtract in smaller parts if needed.",
      example: `Example: 42 - 17 = 42 - 10 - 7 = 25.`,
    };
  }

  if (operation === "multiplication") {
    const a = randInt(min, max);
    const b = randInt(min, max);
    return {
      question: `${a} × ${b}`,
      answer: a * b,
      hint: "Think of multiplication as equal groups or repeated addition.",
      example: `Example: 4 × 6 means 4 groups of 6, which equals 24.`,
    };
  }

  const divisor = randInt(min, max);
  const quotient = randInt(min, max);
  const dividend = divisor * quotient;

  return {
    question: `${dividend} ÷ ${divisor}`,
    answer: quotient,
    hint: "Ask what number times the divisor gives the dividend.",
    example: `Example: 24 ÷ 6 = 4 because 6 × 4 = 24.`,
  };
}

function getAvaResponse(
  isCorrect: boolean | null,
  reveal: boolean,
  current: FlashCard,
  userAnswer: string
): string {
  if (reveal) {
    return `Here is the answer: ${current.question} = ${current.answer}. ${current.example}`;
  }

  if (isCorrect === true) {
    return "Great work. You got it right. Keep going one step at a time.";
  }

  if (isCorrect === false) {
    return `That is okay. Let’s slow it down and try another way. Hint: ${current.hint} ${current.example}`;
  }

  if (!userAnswer.trim()) {
    return "Enter an answer when you're ready. If you want help, use Hint or Reveal.";
  }

  return "Take your time. You can do this. Focus on one part of the problem at a time.";
}

export default function FlashCardsPage() {
  const navigate = useNavigate();
  const { themeColors } = useContext(ThemeContext);
  const {
    pageBg,
    shellStyle,
    panelStyle,
    cardStyle,
    controlStyle,
    buttonStyle,
  } = useMemo(() => makeFlashCardStyles(themeColors), [themeColors]);

  const [operation, setOperation] = useState<Operation>("addition");
  const [grade, setGrade] = useState<GradeBand>("K-2");
  const [answer, setAnswer] = useState("");
  const [reveal, setReveal] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [result, setResult] = useState<boolean | null>(null);
  const [seed, setSeed] = useState(1);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const current = useMemo(() => buildCard(operation, grade), [operation, grade, seed]);

  const avaMessage = useMemo(
    () => getAvaResponse(result, reveal, current, answer),
    [result, reveal, current, answer]
  );

  function resetCard(nextSeed = seed) {
    setAnswer("");
    setReveal(false);
    setShowHint(false);
    setResult(null);
    setSeed(nextSeed);
  }

  function handleCheck() {
    const parsed = Number(answer.trim());
    const correct = parsed === current.answer;
    setResult(correct);
    setScore((prev) => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
    }));
  }

  function handleNext() {
    resetCard(seed + 1);
  }

  function handleOperationChange(value: Operation) {
    setOperation(value);
    resetCard(seed + 1);
  }

  function handleGradeChange(value: GradeBand) {
    setGrade(value);
    resetCard(seed + 1);
  }

  return (
    <div style={pageBg}>
      <div style={shellStyle}>
        <div style={{ ...panelStyle, marginBottom: "14px" }}>
          <div
            style={{
              fontSize: "11px",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: themeColors.accent2,
              fontWeight: 800,
              marginBottom: "6px",
            }}
          >
            Ava Education
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(26px, 4.3vw, 48px)",
              lineHeight: 1,
              fontWeight: 900,
              color: themeColors.accent,
              textShadow: themeColors.glow,
              wordBreak: "break-word",
            }}
          >
            Flash Cards
          </h1>

          <p
            style={{
              margin: "8px 0 0",
              fontSize: "clamp(12px, 1.5vw, 16px)",
              color: themeColors.mutedText,
              maxWidth: "820px",
              lineHeight: 1.4,
            }}
          >
            Practice core math skills with Ava&apos;s supportive tutor style.
          </p>
        </div>

        <div
          className="flashcards-layout"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.7fr) minmax(300px, 0.85fr)",
            gap: "14px",
            alignItems: "start",
          }}
        >
          <div style={cardStyle}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "10px",
                marginBottom: "12px",
              }}
            >
              <div>
                <div style={{ marginBottom: "6px", fontWeight: 800, fontSize: "13px" }}>
                  Operation
                </div>
                <select
                  value={operation}
                  onChange={(e) => handleOperationChange(e.target.value as Operation)}
                  style={controlStyle}
                >
                  <option value="addition">Addition</option>
                  <option value="subtraction">Subtraction</option>
                  <option value="multiplication">Multiplication</option>
                  <option value="division">Division</option>
                </select>
              </div>

              <div>
                <div style={{ marginBottom: "6px", fontWeight: 800, fontSize: "13px" }}>
                  Grade Level
                </div>
                <select
                  value={grade}
                  onChange={(e) => handleGradeChange(e.target.value as GradeBand)}
                  style={controlStyle}
                >
                  <option value="K-2">K–2</option>
                  <option value="3-5">3–5</option>
                  <option value="6-8">6–8</option>
                  <option value="9-12">9–12</option>
                </select>
              </div>
            </div>

            <div
              style={{
                ...panelStyle,
                marginBottom: "12px",
                padding: "14px 12px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  color: themeColors.mutedText,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  marginBottom: "8px",
                  fontWeight: 700,
                }}
              >
                Current Problem
              </div>

              <div
                style={{
                  fontSize: "clamp(34px, 6vw, 70px)",
                  fontWeight: 900,
                  color: themeColors.text,
                  textShadow: themeColors.glow,
                  lineHeight: 1.04,
                  wordBreak: "break-word",
                }}
              >
                {current.question}
              </div>
            </div>

            <div style={{ display: "grid", gap: "10px" }}>
              <input
                type="number"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type your answer"
                style={{
                  ...controlStyle,
                  minHeight: "48px",
                  fontSize: "17px",
                }}
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
                  gap: "8px",
                }}
              >
                <button type="button" onClick={handleCheck} style={buttonStyle}>
                  Check Answer
                </button>

                <button
                  type="button"
                  onClick={() => setShowHint((v) => !v)}
                  style={buttonStyle}
                >
                  {showHint ? "Hide Hint" : "Show Hint"}
                </button>

                <button
                  type="button"
                  onClick={() => setReveal(true)}
                  style={buttonStyle}
                >
                  Reveal
                </button>

                <button type="button" onClick={handleNext} style={buttonStyle}>
                  Next Card
                </button>
              </div>
            </div>

            <div
              style={{
                marginTop: "12px",
                padding: "10px 12px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(90, 220, 255, 0.16)",
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: "4px", fontSize: "13px" }}>
                Progress
              </div>
              <div style={{ color: "rgba(236,250,255,0.8)", lineHeight: 1.4, fontSize: "13px" }}>
                Correct: {score.correct} / {score.total}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: "12px" }}>
            <div style={cardStyle}>
              <div style={{ fontSize: "16px", fontWeight: 900, marginBottom: "6px" }}>
                Ava Tutor
              </div>
              <div
                style={{
                  lineHeight: 1.55,
                  color: "rgba(236,250,255,0.84)",
                  whiteSpace: "pre-wrap",
                  fontSize: "14px",
                }}
              >
                {avaMessage}
              </div>
            </div>

            {showHint && (
              <div style={cardStyle}>
                <div style={{ fontSize: "16px", fontWeight: 900, marginBottom: "6px" }}>
                  Hint
                </div>
                <div
                  style={{
                    lineHeight: 1.55,
                    color: "rgba(236,250,255,0.84)",
                    fontSize: "14px",
                  }}
                >
                  {current.hint}
                </div>
              </div>
            )}

            {reveal && (
              <div style={cardStyle}>
                <div style={{ fontSize: "16px", fontWeight: 900, marginBottom: "6px" }}>
                  Worked Example
                </div>
                <div
                  style={{
                    lineHeight: 1.55,
                    color: "rgba(236,250,255,0.84)",
                    fontSize: "14px",
                  }}
                >
                  Answer: <strong>{current.answer}</strong>
                  <br />
                  <br />
                  {current.example}
                </div>
              </div>
            )}

            <div style={cardStyle}>
              <div style={{ fontSize: "16px", fontWeight: 900, marginBottom: "6px" }}>
                Navigation
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "8px",
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/education")}
                  style={buttonStyle}
                >
                  Back to Education
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/games")}
                  style={buttonStyle}
                >
                  Back to Games
                </button>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 1120px) {
            .flashcards-layout {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}